/**
 * Delegation Selector Component
 *
 * Allows delegates to select which clinician they are acting on behalf of.
 * Displays as "Acting as: [Clinician Name]" dropdown.
 */

import { useAuth0 } from '@auth0/auth0-react'
import { Button } from '@helix-ds/react'
import { ChevronDown } from 'flowbite-react-icons/outline'
import React, { useEffect, useState } from 'react'
import { useAuthorization } from './authorizationHooks'

interface DelegationOption {
  userId: string
  userName: string
  permissions: string[]
}

interface DelegationSelectorProps {
  /** Optional callback when delegation changes */
  onDelegationChange?: (userId: string | null) => void

  /** Custom styling className */
  className?: string
}

export function DelegationSelector({ onDelegationChange, className = '' }: DelegationSelectorProps) {
  const { activeDelegation, availableDelegations, setActiveDelegation, userId } = useAuthorization()

  const [delegationOptions, setDelegationOptions] = useState<DelegationOption[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Fetch delegation options (grantors who have delegated to current user)
  useEffect(() => {
    fetchDelegationOptions()
  }, [availableDelegations])

  async function fetchDelegationOptions() {
    try {
      setIsLoading(true)

      console.log('[DelegationSelector] Available delegations:', availableDelegations)
      console.log('[DelegationSelector] Current userId:', userId)

      // Filter delegations where current user is the grantee and status is active
      const activeDelegationsToMe = availableDelegations.filter(
        (d) => d.granteeUserId === userId && d.status === 'active'
      )

      console.log('[DelegationSelector] Active delegations to me:', activeDelegationsToMe)

      // Map to display options with names
      const options: DelegationOption[] = activeDelegationsToMe.map((d) => ({
        userId: d.grantorUserId,
        userName: d.grantorName || d.grantorEmail || d.grantorUserId, // Use name, fallback to email, then ID
        permissions: d.permissions,
      }))

      console.log('[DelegationSelector] Delegation options:', options)
      setDelegationOptions(options)
    } catch (error) {
      console.error('Failed to fetch delegation options:', error)
    } finally {
      setIsLoading(false)
    }
  }

  function handleDelegationChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const selectedUserId = event.target.value === '' ? null : event.target.value
    setActiveDelegation(selectedUserId)

    if (onDelegationChange) {
      onDelegationChange(selectedUserId)
    }
  }

  // Don't show selector if no delegations available
  if (delegationOptions.length === 0) {
    return null
  }

  return (
    <div
      className={`delegation-selector flex items-center gap-2 rounded-base border border-warning-300 dark:border-warning-600 bg-warning-100 dark:bg-gray-900 px-4 py-2 ${className}`}
    >
      <label
        htmlFor="delegation-select"
        className="whitespace-nowrap font-medium text-text-fg-warning dark:text-warning-100"
      >
        Acting as:
      </label>

      <div className="relative">
        <select
          id="delegation-select"
          value={activeDelegation || ''}
          onChange={handleDelegationChange}
          disabled={isLoading}
          className="min-w-[200px] cursor-pointer appearance-none rounded-xs border border-warning-300 dark:border-warning-600 bg-white dark:bg-gray-900 pl-3 pr-10 py-1.5 text-text-fg-warning dark:text-warning-100"
        >
          <option value="">Myself</option>
          {delegationOptions.map((option) => (
            <option key={option.userId} value={option.userId}>
              {option.userName}
            </option>
          ))}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-fg-warning dark:text-warning-100"
          aria-hidden
        />
      </div>

      {activeDelegation && (
        <span className="text-sm italic text-text-fg-warning dark:text-warning-100">
          (Delegated access)
        </span>
      )}
    </div>
  )
}

/**
 * Delegation Management Component
 *
 * Allows clinicians to grant/revoke delegations.
 */
interface DelegationManagementProps {
  /** Custom styling className */
  className?: string
}

export function DelegationManagement({ className = '' }: DelegationManagementProps) {
  const { availableDelegations, refreshPermissions, hasPermission } = useAuthorization()
  const { getAccessTokenSilently } = useAuth0()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Check if user can grant delegations
  const canGrantDelegations = hasPermission('delegate:grant')

  if (!canGrantDelegations) {
    return null // Don't show if user can't grant delegations
  }

  async function handleRevokeDelegation(delegationId: string) {
    if (!confirm('Are you sure you want to revoke this delegation?')) {
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const token = await getAccessTokenSilently()
      const apiBaseUrl = import.meta.env['VITE_PROVIDER_API_URL'] || import.meta.env['VITE_API_BASE_URL']

      const response = await fetch(`${apiBaseUrl}/auth/delegations/${delegationId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to revoke delegation: ${response.statusText}`)
      }

      // Refresh delegations list
      await refreshPermissions()
    } catch (err) {
      console.error('Failed to revoke delegation:', err)
      setError(err instanceof Error ? err.message : 'Failed to revoke delegation')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`delegation-management ${className}`}>
      <h3 className="mb-4 text-lg font-semibold">My Delegations</h3>

      {error && (
        <div className="mb-4 rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 p-3 text-text-fg-error-strong dark:text-error-100">
          {error}
        </div>
      )}

      {availableDelegations.length === 0 ? (
        <p className="italic text-text-body dark:text-gray-400">No active delegations</p>
      ) : (
        <div className="flex flex-col gap-3">
          {availableDelegations.map((delegation) => (
            <div
              key={delegation.id}
              className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="mb-1 font-medium">To: {delegation.granteeUserId}</div>
                  <div className="text-sm text-text-body dark:text-gray-400">
                    Permissions: {delegation.permissions.join(', ')}
                  </div>
                  <div className="mt-1 text-sm text-text-body dark:text-gray-400">
                    Status: {delegation.status}
                  </div>
                  {delegation.endsAt && (
                    <div className="mt-1 text-sm text-text-body dark:text-gray-400">
                      Expires: {new Date(delegation.endsAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {delegation.status === 'active' && (
                  <Button
                    variant="danger"
                    size="md"
                    onClick={() => handleRevokeDelegation(delegation.id)}
                    disabled={isLoading}
                    className="cursor-pointer focus-visible:ring-error-500"
                  >
                    {isLoading ? 'Revoking...' : 'Revoke'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
